/**
 * Armazon de la aplicacion: barra lateral + barra superior + area de contenido.
 *
 * Responsive de verdad, no "se ve aceptable en un celular": por debajo de `lg` la
 * barra lateral se convierte en un cajon a pantalla completa con cierre por
 * Escape, por toque fuera y por boton. Quien abre esto parado en
 * el palier tiene que poder navegar con una mano.
 *
 * El area de contenido es un `<Outlet/>`: el renderer generico de vistas se monta
 * ahi sin tocar este archivo.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { ChatGuia } from '@/components/guia/ChatGuia';
import { AvisoDemo } from '@/components/shell/AvisoDemo';
import { CommandPalette } from '@/components/shell/CommandPalette';
import { MENU_PARAM } from '@/components/shell/menu-utils';
import { Sidebar } from '@/components/shell/Sidebar';
import { ToastHost } from '@/components/shell/ToastHost';
import { TopBar } from '@/components/shell/TopBar';

/**
 * Modelo, registro y menu de origen de la pantalla actual, leidos de la ruta
 * `/m/<modelo>/<id>?menu=<nombre>`.
 *
 * Se parsea aca en vez de usar `useParams` porque el shell esta por FUERA de las
 * rutas y no tiene acceso a sus parametros.
 */
export function useCurrentLocation(): { model: string | null; recordId: string | null; menu: string | null } {
  const { pathname, search } = useLocation();
  const menu = new URLSearchParams(search).get(MENU_PARAM);
  const partes = pathname.split('/').filter((parte) => parte !== '');
  if (partes[0] !== 'm' || partes[1] === undefined) return { model: null, recordId: null, menu: null };
  return {
    model: decodeURIComponent(partes[1]),
    recordId: partes[2] === undefined ? null : decodeURIComponent(partes[2]),
    menu: menu === '' ? null : menu,
  };
}

export function AppShell({ children }: { children: ReactNode }) {
  const { model: currentModel, recordId, menu: currentMenu } = useCurrentLocation();
  const { pathname } = useLocation();
  const [cajonAbierto, setCajonAbierto] = useState(false);
  const cajonRef = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);

  // Cambiar de pantalla cierra el cajon: dejarlo abierto tapa el contenido al que
  // el usuario acaba de navegar.
  useEffect(() => {
    setCajonAbierto(false);
  }, [pathname]);

  useEffect(() => {
    if (!cajonAbierto) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setCajonAbierto(false);
    };
    document.addEventListener('keydown', alTeclear);

    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      cajonRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.body.style.overflow = overflowPrevio;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [cajonAbierto]);

  return (
    <div className="min-h-svh bg-app">
      <a href="#contenido" className="skip-link">
        Saltar al contenido
      </a>

      {/* Barra lateral fija en escritorio */}
      <div className="fixed inset-y-0 left-0 z-30 hidden w-sidebar border-r border-line lg:block">
        <Sidebar
          currentModel={currentModel}
          currentMenu={currentMenu}
          onNavigate={() => {
            setCajonAbierto(false);
          }}
        />
      </div>

      {/* Cajon en movil y tablet */}
      {cajonAbierto && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-fg/30 animate-fade-in"
            onClick={() => {
              setCajonAbierto(false);
            }}
            aria-hidden="true"
          />
          <div
            ref={cajonRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navegación"
            className="absolute inset-y-0 left-0 w-[min(20rem,85vw)] border-r border-line shadow-lg animate-slide-in-left"
          >
            <Sidebar
              currentModel={currentModel}
              currentMenu={currentMenu}
              onNavigate={() => {
                setCajonAbierto(false);
              }}
              onClose={() => {
                setCajonAbierto(false);
              }}
            />
          </div>
        </div>
      )}

      <div className="lg:pl-sidebar">
        {/* Arriba de todo y en todas las pantallas: si la organización es de
            demostración, hay que enterarse antes de mirar un número. */}
        <AvisoDemo />
        <TopBar
          currentModel={currentModel}
          currentMenu={currentMenu}
          currentRecordId={recordId}
          onOpenSidebar={() => {
            setCajonAbierto(true);
          }}
        />
        <main id="contenido" tabIndex={-1} className="mx-auto w-full max-w-content px-4 py-6 sm:px-6 sm:py-8">
          {children}
        </main>
      </div>

      <CommandPalette />
      {/* La ayuda va acá, al lado de la paleta y por fuera del contenido: tiene
          que estar en TODAS las pantallas del ERP, y tiene que seguir estando
          cuando la pantalla de adentro se cae (la frontera de errores envuelve
          sólo al contenido). Si el despliegue no sirve la guía, no se dibuja
          nada. Ver `components/guia/ChatGuia`. */}
      <ChatGuia />
      <ToastHost />
    </div>
  );
}
